import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Calculator, Moon, Sun, User } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

const links = [
  { to: "/calculators", label: "Calculators" },
  { to: "/compare", label: "Compare" },
  { to: "/guides", label: "Guides" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const { user } = useAuth();

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const navClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-primary ${
      isActive ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Calculator className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">ProfitMigo</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={navClass} data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button asChild variant="ghost" size="icon" className="hidden md:inline-flex" data-testid="header-account">
            <Link to={user ? "/products" : "/account"} aria-label="Account"><User className="h-4 w-4" /></Link>
          </Button>
          <Button asChild className="hidden md:inline-flex" data-testid="header-cta">
            <Link to="/ecommerce/product-profit-calculator">Open calculator</Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Menu" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="mt-8 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="rounded-md px-3 py-3 text-base font-medium hover:bg-secondary"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Button asChild className="mt-4" onClick={() => setOpen(false)}>
                  <Link to="/ecommerce/product-profit-calculator">Open calculator</Link>
                </Button>
                <Button asChild variant="outline" className="mt-2" onClick={() => setOpen(false)} data-testid="mobile-account">
                  <Link to={user ? "/products" : "/account"}>{user ? "My Products" : "Sign in"}</Link>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
